import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Glyph } from '@/components/icons';
import { MAIN_SLOT } from '@/components/TabBar';
import { prefersReducedMotion } from '@/hooks';
import type { Strings } from '@/str';

import { audioGlyph, audioTime } from './format';
import { PlayGlyph } from './parts';
import type { ExplainAudio } from './useExplainAudio';

export function MiniPlayer({
  audio,
  t,
  onOpen,
}: {
  audio: ExplainAudio;
  t: Strings;
  onOpen: () => void;
}) {
  const root = useRef<HTMLDivElement>(null);
  const [host] = useState(() => {
    const el = document.createElement('div');
    if (!document.getElementById(MAIN_SLOT)) el.className = 'fixed inset-x-0 bottom-0 z-50';
    return el;
  });
  const track = audio.track;

  useLayoutEffect(() => {
    const slot = document.getElementById(MAIN_SLOT);
    if (slot?.parentElement) slot.parentElement.insertBefore(host, slot);
    else document.body.appendChild(host);
    return () => host.remove();
  }, [host]);

  if (!track) return null;

  const hide = () => {
    const el = root.current;
    if (!el || prefersReducedMotion() || typeof el.animate !== 'function') return audio.stop();
    el.animate(
      [
        { opacity: 1, transform: 'translateY(0)' },
        { opacity: 0, transform: 'translateY(14px)' },
      ],
      { duration: 180, easing: 'cubic-bezier(.4,0,1,1)', fill: 'forwards' },
    );
    window.setTimeout(audio.stop, 170);
  };

  const bar = (
    <div
      ref={root}
      data-mini
      className="bg-card border-border-strong animate-rise flex items-center gap-2.75 border-t px-3.5 py-2.25"
    >
      <button
        type="button"
        onClick={() => audio.toggle(track)}
        aria-label={t.exp.voice}
        aria-pressed={audio.playing}
        className="bg-primary text-primary-foreground text-t12 flex size-11 flex-none items-center justify-center rounded-full border-0 font-extrabold active:scale-[.94]"
      >
        <PlayGlyph state={audioGlyph(audio, true)} size={13} />
      </button>
      <button
        type="button"
        data-mini-open
        onClick={onOpen}
        className="text-foreground flex min-h-11 min-w-0 flex-1 flex-col justify-center gap-1.25 border-0 bg-transparent p-0 text-left"
      >
        <span className="flex items-baseline justify-between gap-2">
          <span className="text-t12 truncate font-extrabold tracking-[-0.01em]">
            {t.exp.voice} · {t.qNum.split('{n}').join(String(track.index + 1))}
          </span>
          <span className="text-t12 text-faint flex-none font-extrabold tabular-nums">
            {audioTime(audio, track.duration, true, t)}
          </span>
        </span>
        <span className="bg-muted block h-0.75 overflow-hidden rounded-[2px]">
          <span
            className="bg-primary block h-full w-full origin-left transition-transform duration-250 ease-linear"
            style={{ transform: `scaleX(${audio.progress.toFixed(3)})` }}
          />
        </span>
      </button>
      <button
        type="button"
        onClick={hide}
        aria-label={t.exp.stop}
        className="border-border-strong text-muted-foreground flex size-11 flex-none items-center justify-center rounded-xs border bg-transparent active:scale-[.94]"
      >
        <Glyph name="close" size={13} weight={2.2} round={false} />
      </button>
    </div>
  );

  return createPortal(bar, host);
}
